import { makeAutoObservable, runInAction } from 'mobx'
import CasesService from '../../drivers/services/CasesService'
import responseStatusStore from '../../lib/stores/ResponseStatusStore'
import constants, { PAYMENT_METHOD_CREDIT_CARD, PAYMENT_METHOD_CASH } from '../util/constants'
const responseStatus = new responseStatusStore()

class CaseDetailStore {
  constructor () {
    this.immobilizationCase = null
    this.isLoading = false
    this.isLoadingDropOffLocations = false
    this.dropOffLocationsError = false
    this.selectedReleaseMethod = constants.releaseMethodCodes.Manual
    this.caseLoadError = null
    this.dropOffLocations = []
    this.selectedPaymentMethod = null
    this.cancelingCashPayment = false
    this.serverError = ''
    this.billsLoaded = false

    makeAutoObservable(this)

    this.casesService = new CasesService()
  }

  selectPaymentMethod (paymentMethod) {
    this.selectedPaymentMethod = paymentMethod
  }

  setCashPaymentMethod () {
    this.selectPaymentMethod(PAYMENT_METHOD_CASH)
  }

  setCreditCardPaymentMethod () {
    this.selectPaymentMethod(PAYMENT_METHOD_CREDIT_CARD)
  }

  resetPaymentMethod () {
    this.selectPaymentMethod(null)
  }

  get isCashPayment () {
    return this.selectedPaymentMethod === PAYMENT_METHOD_CASH
  }

  setCase (immobilizationCase) {
    this.immobilizationCase = immobilizationCase
  }

  clearCaseLoadError () {
    this.caseLoadError = null
  }

  setBillsLoaded (val) {
    this.billsLoaded = val
  }

  selectReleaseMethod (releaseMethod) {
    this.selectedReleaseMethod = releaseMethod
    this.billsLoaded = false
  }

  clearServerError () {
    this.serverError = ''
  }

  async loadCaseBySlug (slug, releaseMethod) {
    this.isLoading = true
    this.caseLoadError = null

    try {
      const immobilizationCase = await this.casesService.loadBySlug(
        slug,
        releaseMethod || this.selectedReleaseMethod
      )

      runInAction(() => {
        this.immobilizationCase = immobilizationCase
        this.isLoading = false
        this.billsLoaded = true
      })
    } catch (e) {
      runInAction(() => {
        if (e?.response?.status === 404) {
          this.caseLoadError = 'The QR Code is invalid'
        } else if (e?.response?.status === 422) {
          // responseStatus.setApplicationError(e?.response?.data?.message)
          this.caseLoadError = e?.response?.data?.message
        } else {
          responseStatus.setError500()
        }

        this.isLoading = false
      })
    }
  }

  getReleaseCode () {
    return this.casesService.getReleaseCode(this.immobilizationCase.slug)
  }

  get selfReleaseAvailable () {
    return (
      this.immobilizationCase.operationVariables.available_release_methods !==
      constants.releaseMethodCodes.Assisted
    )
  }

  get assistedReleaseAvailable () {
    return (
      this.immobilizationCase.operationVariables.available_release_methods !==
      constants.releaseMethodCodes.Manual
    )
  }

  get releaseAmountDue () {
    const citationsTotal = this.immobilizationCase.amountDue

    if (this.selectedReleaseMethod) {
      return citationsTotal + this.getReleasePrice(this.selectedReleaseMethod)
    }

    return citationsTotal
  }

  get minutesToAssistRelease () {
    return this.immobilizationCase.minutesToAssistRelease
  }

  get isAssistedRelease () {
    return this.selectedReleaseMethod === constants.releaseMethodCodes.Assisted
  }

  get isSelfRelease () {
    return this.selectedReleaseMethod === constants.releaseMethodCodes.Manual
  }

  getReleasePrice (releaseMethod) {
    if (releaseMethod === constants.releaseMethodCodes.Assisted) {
      return parseFloat(this.immobilizationCase.assistedReleaseCost)
    }
    return 0
  }

  async getDropOffLocations () {
    this.isLoadingDropOffLocations = true
    this.dropOffLocationsError = false

    try {
      const dropOffLocations = await this.casesService.getDropOffLocations(
        this.immobilizationCase.operationId
      )

      runInAction(() => {
        this.dropOffLocations = dropOffLocations
        this.isLoadingDropOffLocations = false
      })
    } catch (e) {
      runInAction(() => {
        this.isLoadingDropOffLocations = false
        this.dropOffLocationsError = true
      })
    }

    return false
  }

  async cancelCashPayment () {
    this.cancelingCashPayment = true

    try {
      await this.casesService.cancelCashPayment(this.immobilizationCase.slug)

      runInAction(() => {
        this.cancelingCashPayment = false
      })

      return true
    } catch (e) {
      runInAction(() => {
        if (e.response?.data) {
          this.serverError = e.response.data
        }
        this.cancelingCashPayment = false
      })
    }
    return false
  }

  async loadCaseBills (releaseMethod) {
    this.isLoading = true
    this.caseLoadError = null
    this.billsLoaded = false

    try {
      const immobilizationCase = await this.casesService.loadCaseBills(
        this.immobilizationCase.slug,
        releaseMethod || this.selectedReleaseMethod
      )

      runInAction(() => {
        this.immobilizationCase = immobilizationCase
        this.isLoading = false
        this.billsLoaded = true
      })
    } catch (e) {
      runInAction(() => {
        if (e?.response?.status === 422) {
          this.caseLoadError = e?.response?.data?.message
        } else {
          responseStatus.setError500()
        }

        this.isLoading = false
      })
    }
  }

  async getSelfReleaseDescription() {
    const description = await this.casesService.getSelfReleaseDescription(this.immobilizationCase.operation)
    if (description.success) {
      return description.data.rule.self_release_description
    }

    return description.error
  }
}

export default CaseDetailStore
